import { useState, useEffect } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { TextInput, NumberInput, ActionIcon, Tooltip, Group, Text } from '@mantine/core';
import { IconTrash, IconEdit, IconCheck, IconX } from '@tabler/icons-react';
import type { IConfiguracionMoiAscensor } from '../interfaces';

export interface MoiAscensorTableMeta {
  actualizarFila: (id: string, campo: keyof IConfiguracionMoiAscensor | string, valor: string | number) => void;
  eliminarFila: (id: string) => void;
  guardarMatriz?: () => void;
  filaEnEdicion?: string | null;
  iniciarEdicion?: (fila: any) => void;
  cancelarEdicion?: () => void;
  guardarFila?: (id: string) => void;
  eliminarCapacidad: (capacidad: string) => void;
  iniciarEdicionCapacidad?: (capacidad: string) => void;
  mostrarNotificacionObligatorio?: () => void;
  intentoGuardar?: boolean;
}

/** Días con su unidad: "1 día", "15 días". */
const formatoDias = (dias: number) => `${dias.toLocaleString('es-CO')} ${dias === 1 ? 'día' : 'días'}`;

const EditableTextCell = ({ getValue, row, column, table }: any) => {
  const initialValue = typeof getValue() === 'object' ? getValue()[column.id] : getValue();
  const [value, setValue] = useState(initialValue);
  const meta = table.options.meta as MoiAscensorTableMeta | undefined;
  const campo = column.id;
  const rowId = row.original.Id;

  useEffect(() => { setValue(initialValue); }, [initialValue]);

  const isEditing = String(meta?.filaEnEdicion) === String(rowId);
  if (!isEditing) {
    return <Text size="xs">{value || "-"}</Text>;
  }
  return (
    <TextInput
      size="xs"
      value={value || ""}
      placeholder={`${campo} *`}
      required
      withAsterisk
      onChange={(e) => { setValue(e.currentTarget.value); if (meta) meta.actualizarFila(rowId, campo, e.currentTarget.value); }}
      error={(meta?.intentoGuardar) && (!value || String(value).trim() === "")}
      styles={{ input: { fontSize: "12px", padding: "4px 8px" } }}
    />
  );
};

const EditableNumberCell = ({ getValue, row, column, table }: any) => {
  const initialValue = getValue() as number;
  const [value, setValue] = useState<number | string>(initialValue);
  const [touched, setTouched] = useState(false);
  const meta = table.options.meta as MoiAscensorTableMeta | undefined;
  const campo = column.id as keyof IConfiguracionMoiAscensor;
  const rowId = row.original.Id;

  useEffect(() => { setValue(initialValue); }, [initialValue]);

  const isEditing = String(meta?.filaEnEdicion) === String(rowId);
  if (!isEditing) {
    if (value === null || value === undefined || value === "") return <Text size="xs">-</Text>;
    return <Text size="xs">{formatoDias(Number(value))}</Text>;
  }
  return (
    <NumberInput
      size="xs"
      hideControls
      value={value !== 0 ? value : (value === 0 ? "0" : "")}
      onChange={(val) => { setTouched(true); setValue(val); if (meta) meta.actualizarFila(rowId, campo, val === "" ? "" : (Number(val) || 0)); }}
      placeholder="Días *"
      suffix=" días"
      required
      withAsterisk
      error={(touched || meta?.intentoGuardar) && (value === null || value === undefined || String(value) === "" || Number(value) <= 0)}
      styles={{ input: { fontSize: "12px", padding: "4px 8px" } }}
    />
  );
};

export const obtenerColumnasMoiAscensor = (capacidades: string[]): ColumnDef<IConfiguracionMoiAscensor>[] => {
  const columnasFijas: ColumnDef<IConfiguracionMoiAscensor>[] = [
    {
      id: "acciones",
      header: "Acciones",
      size: 90,
      cell: ({ row, table }) => {
        const meta = table.options.meta as MoiAscensorTableMeta | undefined;
        const isEditing = String(meta?.filaEnEdicion) === String(row.original.Id);
        const hayOtraFilaEnEdicion = meta?.filaEnEdicion !== null && meta?.filaEnEdicion !== undefined && String(meta?.filaEnEdicion) !== String(row.original.Id);
        
        if (isEditing) {
          return (
            <Group gap={4} justify="center">
              <Tooltip label="Guardar" withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
                <ActionIcon variant="subtle" color="var(--mantine-color-primary-0)" size="sm" radius="xl" onClick={() => {
                  const data = row.original;
                  const paradasVacio = !data.Paradas || String(data.Paradas).trim() === '';
                  // Los días de cada capacidad de la tabla son obligatorios (mayores a 0), aunque la fila no la tenga aún.
                  const capacidadesVacias = capacidades.some(cap => {
                    const dias = data.Capacidades[cap];
                    return dias === null || dias === undefined || String(dias).trim() === '' || Number(dias) <= 0;
                  });
                  
                  if (paradasVacio || capacidadesVacias) {
                    meta?.mostrarNotificacionObligatorio?.();
                    return;
                  }
                  meta?.guardarFila?.(data.Id);
                }}>
                  <IconCheck size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Cancelar" withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
                <ActionIcon variant="subtle" color="#df675a" size="sm" radius="xl" onClick={() => meta?.cancelarEdicion?.()}>
                  <IconX size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          );
        }
        return (
          <Group gap={4} justify="center">
            <Tooltip label={hayOtraFilaEnEdicion ? "Termina la edición actual" : "Editar"} withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
               <ActionIcon disabled={hayOtraFilaEnEdicion} variant="transparent" color="var(--mantine-color-primary-0)" size="sm" radius="sm" onClick={() => meta?.iniciarEdicion?.(row.original)}>
                 <IconEdit size={18} />
               </ActionIcon>
            </Tooltip>
            <Tooltip label={hayOtraFilaEnEdicion ? "Termina la edición actual" : "Eliminar"} withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
              <ActionIcon disabled={hayOtraFilaEnEdicion} variant="transparent" color="var(--mantine-color-primary-0)" size="sm" radius="sm" onClick={() => meta?.eliminarFila(row.original.Id)}>
                <IconTrash size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        );
      }
    },
    {
      accessorKey: "Paradas",
      id: "Paradas",
      header: "Paradas",
      cell: EditableTextCell,
      size: 100,
    }
  ];

  const columnasCapacidades: ColumnDef<IConfiguracionMoiAscensor>[] = capacidades.map((capacidad) => ({
    id: capacidad,
    accessorFn: (row) => row.Capacidades[capacidad],
    header: ({ table }) => {
      const meta = table.options.meta as MoiAscensorTableMeta | undefined;
      return (
        <Group gap="xs" justify="center" wrap="nowrap">
          <span>Cap. {capacidad}kg</span>
          <Group gap={2} wrap="nowrap">
            <ActionIcon 
              size="xs" 
              color="var(--mantine-color-primary-0)" 
              variant="transparent" 
              onClick={() => meta?.iniciarEdicionCapacidad?.(capacidad)}
            >
              <IconEdit size={14} />
            </ActionIcon>
            <ActionIcon 
              size="xs" 
              color="#df675a" 
              variant="transparent" 
              onClick={() => {
                if(capacidades.length <= 1) {
                  alert("Debe existir al menos una capacidad.");
                  return;
                }
                meta?.eliminarCapacidad(capacidad);
              }}
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Group>
        </Group>
      );
    },
    cell: EditableNumberCell,
    size: 130,
  }));

  return [...columnasFijas, ...columnasCapacidades];
};
